import { useState, useCallback, useEffect, useMemo } from 'react';
// @mui
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// hooks
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
  useTable,
  emptyRows,
  TableNoData,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  // TableSelectedAction, // Đã loại bỏ
  TablePaginationCustom,
} from 'src/components/table';
// types
import { LoSam } from 'src/types/losam';
// api
import DarkModeButton from 'src/components/button/button-dark-model';
import { useLoSam } from '../hooks/use-losam';
// sections
import LoSamTableRow from '../components/lo-sam-table-row';
import LoSamTableToolbar from '../components/lo-sam-table-toolbar';
// import { ConfirmDialog } from 'src/components/custom-dialog'; // Loại bỏ confirm cho xóa nhiều

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'tenLo', label: 'Tên lô sâm' },
  { id: 'loai', label: 'Loại' },
  { id: 'dienTich', label: 'Diện tích (m²)', align: 'center' },
  { id: 'ghiChu', label: 'Ghi chú' },
  { id: 'kichthuoc', label: 'Kích thước' },
  { id: 'trangThai', label: 'Trạng thái', align: 'center' },
  { id: '', width: 88 },
];

const defaultFilters = { 
  name: '',
  status: 0, // Đặt lại thành 0 (Tất cả) để khớp với logic Vườn trồng
};

// ----------------------------------------------------------------------

export default function ListLoSamView() {
  const table = useTable();
  const settings = useSettingsContext();
  const router = useRouter();
  // const confirm = useBoolean(); // Loại bỏ state confirm cho xóa nhiều

  const [tableData, setTableData] = useState<LoSam[]>([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [loading, setLoading] = useState(true);
  const { listLoSamFlat } = useLoSam();

  // --- Xử lý lọc dữ liệu ---

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
  });

  const dataCounts = useMemo(() => ({
      total: tableData.length,
      active: tableData.filter(row => row.trangThai === 1).length,
      paused: tableData.filter(row => row.trangThai === 2).length, 
      inactive: tableData.filter(row => row.trangThai === 3).length, 
    }), [tableData]);


  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 56 : 76;

  // Cập nhật canReset để kiểm tra cả name và status (0 là 'all')
  const canReset = !!filters.name || filters.status !== 0;

  // Cập nhật notFound
  const notFound = (!dataFiltered.length && canReset) || (dataFiltered.length === 0 && !canReset && !loading);

  const handleFilters = useCallback(
    // Chấp nhận value là string (từ TextField) hoặc number (từ Tabs/Select, nếu dùng)
    (name: string, value: any) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  // --- Xử lý xóa và chỉnh sửa (Giữ lại xóa đơn lẻ) ---

  const handleDeleteRow = useCallback(
    (id: string) => {
      // TODO: Thêm logic gọi API xóa đơn lẻ
      const deleteRow = tableData.filter((row) => row.loSam_ID !== parseInt(id, 10));
      setTableData(deleteRow);
      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, table, tableData]
  );
  
  // Loại bỏ hàm handleDeleteRows (Đã bị comment)
  

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.losam.edit(parseInt(id, 10)));
    },
    [router]
  );

  const handleViewRow = useCallback(
    (id: string) => {
      // Chức năng này không được dùng trong phiên bản hiện tại
      console.log('View row:', id);
    },
    []
  );

  // --- Lấy dữ liệu API ---
  
  const fetchLoSamData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listLoSamFlat({ skip: 0, take: 1000 });
      setTableData(data);
    } catch (error) {
      console.error('Error fetching LoSam data:', error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  }, [listLoSamFlat]);

  useEffect(() => {
    fetchLoSamData();
  }, [fetchLoSamData]);

  // --- Render View ---

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Danh sách lô sâm"
          links={[
            { name: 'Dashboard', href: paths.dashboard.root },
            { name: 'Lô sâm', href: paths.dashboard.losam.root },
            { name: 'Danh sách' },
          ]}
          action={
            <DarkModeButton
              component={RouterLink}
              href={paths.dashboard.losam.new}
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
            >
              Thêm lô sâm
            </DarkModeButton>
          }
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <Card>
          <LoSamTableToolbar
            filters={filters as any} // Ép kiểu tạm thời để tránh lỗi TS nếu LoSamTableToolbar chưa được cập nhật hoàn toàn
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            canReset={canReset}
            numSelected={0} 
            dataCounts={dataCounts} 
          />
          
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  onSort={table.onSort}
                />

                <TableBody>
                  {dataInPage.map((row) => (
                    <LoSamTableRow
                      key={row.loSam_ID || 'unknown'}
                      row={row}
                      onDeleteRow={() => handleDeleteRow(row.loSam_ID?.toString() || '')}
                      onEditRow={() => handleEditRow(row.loSam_ID?.toString() || '')}
                      onViewRow={() => handleViewRow(row.loSam_ID?.toString() || '')}
                    />
                  ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                  />

                  <TableNoData notFound={notFound} content="Không tìm thấy dữ liệu" />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={dataFiltered.length}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>

    </Container>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filters,
}: {
  inputData: LoSam[];
  comparator: (a: any, b: any) => number;
  filters: { name: string; status: number }; // Giữ type status là number
}) {
  const { name, status } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (name) {
    inputData = inputData.filter(
      (losam) =>
        losam.tenLo?.toLowerCase().indexOf(name.toLowerCase()) !== -1 ||
        losam.vuonTrongs?.[0]?.tenVuon?.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }

  // Áp dụng logic lọc trạng thái 1, 2, 3 từ VuonTrong
  if (status !== 0) {
    inputData = inputData.filter((losam) => {
      if (status === 1) return losam.trangThai === 1; // Sử dụng
      if (status === 2) return losam.trangThai === 2; // Tạm ngưng
      if (status === 3) return losam.trangThai === 3; // Không sử dụng
      return true;
    });
  }

  return inputData;
}